import { z } from 'zod';
import { TEMPLATE_FILE_CATEGORY, fileCategoryLabels, fileCategorySchema, isSegregatedCategory } from '../files/categories.js';
import { CALENDAR_DATE } from '../time/agency-dates.js';
import { uploadMimeSchema } from '../uploads/allowlist.js';
import { stepStateSchema } from './contract.js';
/**
 * Ad-hoc document requests (#62, PLAN §7.3): HR asking ONE person for ONE document no catalog
 * step covers.
 *
 * Not an exception to the definition model. The request becomes a real StepDefinition head and
 * sealed version — same three tiers, same canonical content and hash as a catalog definition —
 * distinguished only by `scope: { kind: 'person', personId }`, and an instance pinned to it with
 * `adHoc: true`. That is what keeps an ad-hoc submission as immutable as any other.
 *
 * Three guards, all structural:
 *  1. It is a `document-upload`, the one registered step type Phase 2 exposes for this, and the
 *     form below is a constrained editor over that type's own config — never a free-form shape.
 *  2. It satisfies NOTHING: `satisfies` is forced to `[]` and `requirementKey` to null, so an
 *     unversioned one-off can never close a compliance requirement or mint a credential.
 *  3. Because it satisfies nothing it is not a checklist row. It is listed beside the checklist,
 *     never merged into it.
 */
export const ADHOC_STEP_TYPE = 'document-upload';
/**
 * Where an ad-hoc file may be kept: every file category except the agency's template library,
 * which ADR-009 reaches only through an explicit template path, never through a request.
 */
export const adHocFileCategories = fileCategorySchema.options.filter((category) => category !== TEMPLATE_FILE_CATEGORY);
export const adHocFileCategorySchema = z.enum(adHocFileCategories);
/** The form's choices, labelled from shared data so feature code names no category. */
export const adHocFileCategoryOptions = adHocFileCategories.map((category) => ({
    category,
    label: fileCategoryLabels[category],
    segregated: isSegregatedCategory(category),
}));
/** Said wherever somebody tries to make a one-off request close a requirement. */
export const ADHOC_REQUIREMENT_REFUSAL = 'A one-off request cannot satisfy a checklist requirement. If this document is a compliance requirement, ask for it to be added to the requirement catalog (lane:strategy) so it applies to everyone it should, with its own rules and expiry.';
/** The keys a request must not carry, checked BEFORE parsing so the refusal can name the catalog. */
export const adHocForbiddenKeys = ['satisfies', 'requirementKey'];
export const adHocRequestSchema = z
    .object({
    /** One id per request: a retried click is the same request, not a second one. */
    operationId: z.string().uuid(),
    title: z.string().trim().min(1, 'Add a title').max(120),
    instructions: z.string().trim().max(2000).default(''),
    acceptedTypes: z.array(uploadMimeSchema).min(1, 'Choose at least one file type').max(6),
    fileCategory: adHocFileCategorySchema,
    dueDate: z.string().regex(CALENDAR_DATE, 'Use a calendar date').nullable().default(null),
})
    .strict();
const ADHOC_DEFINITION_PREFIX = 'adhoc-';
/** The definition and instance ids a request writes, derived so a retry meets its own records. */
export function adHocDefinitionId(operationId) {
    return `${ADHOC_DEFINITION_PREFIX}${operationId}`;
}
/**
 * True only for an instance a one-off document request created.
 *
 * `adHoc` alone is NOT enough: external checkpoints, HR direct records and the offer-letter form
 * also create `adHoc: true` instances, and some of those DO count toward the checklist. A one-off
 * request is recognised by all three things it is made of — the ad-hoc flag, the
 * `document-upload` type, and a definition id minted by `adHocDefinitionId`.
 */
export function isAdHocRequest(instance) {
    return instance.adHoc && instance.type === ADHOC_STEP_TYPE && instance.pinned.definitionId.startsWith(ADHOC_DEFINITION_PREFIX);
}
/** The `document-upload` config a request seals. `requirementKey` is null by construction. */
export function adHocConfig(request) {
    return {
        heading: request.title,
        instructions: request.instructions,
        acceptedTypes: [...request.acceptedTypes],
        target: request.fileCategory,
        requirementKey: null,
        collectsExpiry: false,
        ...(request.dueDate === null ? {} : { dueDate: request.dueDate }),
    };
}
/** One row of a person's outstanding and past one-off requests, for HR. */
export const adHocRequestSummarySchema = z
    .object({
    instanceId: z.string().min(1),
    title: z.string(),
    state: stepStateSchema,
    fileCategory: fileCategorySchema.nullable(),
    categoryLabel: z.string().nullable(),
    segregated: z.boolean(),
    dueDate: z.string().nullable(),
    issuedAt: z.string().datetime(),
    submittedAt: z.string().datetime().nullable(),
    reviewedAt: z.string().datetime().nullable(),
})
    .strict();
export const adHocRequestListSchema = z.object({ requests: z.array(adHocRequestSummarySchema) }).strict();
/** The states in which the request is still waiting on somebody. */
export const OPEN_ADHOC_STATES = ['not_started', 'sent', 'in_progress', 'submitted', 'rejected'];
/** HR's row for one ad-hoc instance, read off its PINNED config. */
export function summarizeAdHoc(instance) {
    const config = z.object({ target: fileCategorySchema.optional(), dueDate: z.string().optional() }).passthrough().safeParse(instance.config);
    const category = config.success ? (config.data.target ?? null) : null;
    return adHocRequestSummarySchema.parse({
        instanceId: instance.id,
        title: instance.pinned.presentationSnapshot.heading,
        state: instance.state,
        fileCategory: category,
        categoryLabel: category === null ? null : fileCategoryLabels[category],
        segregated: category !== null && isSegregatedCategory(category),
        dueDate: config.success ? (config.data.dueDate ?? null) : null,
        issuedAt: instance.issuedAt,
        submittedAt: instance.submittedAt,
        reviewedAt: instance.reviewedAt,
    });
}
//# sourceMappingURL=adhoc.js.map